"use client";

import { memo, useEffect, useMemo, useRef, useState } from "react";

import NoData from "@/components/profile-performance/noData";

import { TimePeriod } from "../../types";
import SearchAppearancesChart from "./SearchAppearancesChart";
import SearchKeywordsList from "./SearchKeywordsList";

type SearchAppearancesContentProps = {
  data: {
    totalSearches: number;
    changeSinceLastWeek: string;
    timePeriod: number;
    searchKeywords: Array<{ keyword: string; keywordCount: number }>;
    searchTrends: Array<{ period: string; totalCount: number }>;
  };
  timePeriod: TimePeriod;
  onTimePeriodChange: (period: TimePeriod) => void;
};

const DROPDOWN_OPTIONS: Array<{ label: string; value: TimePeriod }> = [
  { label: "90 days", value: "90" },
  { label: "30 days", value: "30" },
  { label: "7 days", value: "7" },
];

const DownArrowIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="#6E00BE"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChangeArrowIcon = ({ isPositive }: { isPositive: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={isPositive ? "-rotate-90" : "rotate-90"}
  >
    <path
      d="M3 8H13M13 8L9 4M13 8L9 12"
      stroke={isPositive ? "#2BA682" : "#E03923"}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchAppearancesContent: React.FC<SearchAppearancesContentProps> = ({
  data,
  timePeriod,
  onTimePeriodChange,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const changeSinceLastWeekValue = useMemo(() => {
    return parseInt(data.changeSinceLastWeek, 10) || 0;
  }, [data.changeSinceLastWeek]);

  const isPositiveChange = changeSinceLastWeekValue >= 0;

  const changePercentage = Math.abs(changeSinceLastWeekValue);

  const currentLabel =
    DROPDOWN_OPTIONS.find((o) => o.value === timePeriod)?.label || "90 days";

  const handleSelect = (value: TimePeriod) => {
    onTimePeriodChange(value);
    setIsDropdownOpen(false);
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-[10px]">
        <div className="flex items-center gap-[6px]">
          <span className="text-base font-bold leading-6 text-[#17142A] md:!text-2xl md:!leading-9">
            {data.totalSearches.toLocaleString()}
          </span>
          <span className="text-sm font-bold leading-5 text-[#17142A] md:!text-base md:!leading-6">
            Search appearance in last
          </span>
        </div>

        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen((p) => !p)}
            className="flex cursor-pointer items-center gap-1 rounded-3xl border-none bg-transparent px-3 py-2 transition-opacity hover:opacity-80"
          >
            <span className="text-sm font-normal leading-5 text-[#6E00BE]">
              {currentLabel}
            </span>
            <DownArrowIcon />
          </button>

          {isDropdownOpen && (
            <div className="absolute right-0 z-10 mt-1 min-w-[120px] overflow-hidden rounded-lg border border-[#E8E8EB] bg-white shadow-lg">
              {DROPDOWN_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`flex w-full items-center px-4 py-2 text-left text-sm leading-5 transition-colors hover:bg-[#F7F8FA] ${
                    option.value === timePeriod
                      ? "bg-[#F7F8FA] font-medium text-[#6E00BE]"
                      : "bg-transparent font-normal text-[#17142A]"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {data.totalSearches && data.totalSearches !== 0 ? (
        <div className="mt-[6px] flex items-center gap-0.5">
          <ChangeArrowIcon isPositive={isPositiveChange} />
          <span className="text-sm font-medium leading-5">
            <span
              className={isPositiveChange ? "text-[#2BA682]" : "text-[#E03923]"}
            >
              {changePercentage}%
            </span>
            <span className="text-[#59566C]"> since last week</span>
          </span>
        </div>
      ) : null}
      <div className="mt-4 h-px bg-[#E8E8EB] md:!mt-5" />
      {!data.totalSearches || data.totalSearches == 0 ? (
        <NoData
          heading="Let’s get your profile noticed!"
          subHeading="You haven't appeared in recruiter searches yet. Keep your profile active and up-to-date to start appearing in recruiter searches."
          buttonLabel="Update Profile to Get Noticed"
          buttonUrl="/seeker/profile"
        />
      ) : (
        <>
          <div className="my-4 text-sm font-normal leading-5 text-[#17142A] md:!my-5 md:!font-bold">
            Your profile appeared in these searches most frequently
          </div>

          <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-8">
            {data.searchKeywords && data.searchKeywords?.length > 0 && (
              <div className="w-full min-w-0 md:max-w-[356px]">
                <SearchKeywordsList keywords={data.searchKeywords} />
              </div>
            )}

            <div className="w-full min-w-0 rounded-xl border border-[#E8E8EB] md:max-w-[434px] md:flex-1">
              <div className="h-[242px] w-full">
                <SearchAppearancesChart
                  data={data.searchTrends}
                  timePeriod={timePeriod}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default memo(SearchAppearancesContent);
